import {
  collection,
  getDocs,
  limit as firestoreLimit,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import {db} from '../firebase';
import {
  newEncounterFirstNamePrefix,
  newEncounterPatientMatchesNamePrefix,
} from '../components/planner/newEncounterPatientSearch';
import {normalizePatientNameParts} from '../utils/patientNames';
import {logger, LogFlow, describeError} from './logger';

const log = logger.scope(LogFlow.PatientSearch);

const DEFAULT_RESULT_LIMIT = 25;
const FIRESTORE_FETCH_MULTIPLIER = 4;

const cleanValue = (value: unknown) => String(value ?? '').trim();

const normalizeLinkedEhrId = (value: unknown) => {
  const trimmed = cleanValue(value);
  return trimmed || null;
};

export function mapNewEncounterEhrPatient(
  documentId: string,
  data: Record<string, any> = {},
) {
  const ehrSourceId = normalizeLinkedEhrId(
    data.ehr_source_id || data.ehrSourceId,
  );
  const rawMrn = cleanValue(data.mrn) || null;
  const rawAccountNo = cleanValue(data.accountNo) || null;
  const name = normalizePatientNameParts({
    firstName: data.firstName || data.first_name,
    middleName: data.middleName || data.middle_name,
    lastName: data.lastName || data.last_name,
    fullName: data.fullName || data.full_name,
    displayName: data.displayName,
    patientName: data.patientName || data.patient_name,
  });

  return {
    id: ehrSourceId || documentId,
    cacheDocumentId: documentId,
    ehrSourceId,
    ehrType: data.ehr_type || null,
    firstName: name.firstName || null,
    lastName: name.lastName || null,
    middleName: name.middleName || null,
    displayName: name.displayName || null,
    fullName: name.fullName || null,
    patientName: name.patientName || null,
    dob: data.dob || null,
    gender: data.gender || null,
    email: data.email || null,
    phone: data.phone || null,
    mrn: rawMrn || rawAccountNo,
    accountNo: rawAccountNo,
    language: data.language || null,
    pcp: data.pcp || null,
    pcpId: data.pcpId || null,
  };
}

const newEncounterEhrPatientKey = (patient: Record<string, any>) =>
  patient.ehrSourceId ||
  patient.id ||
  patient.cacheDocumentId ||
  patient.mrn ||
  patient.accountNo ||
  '';

const dedupeNewEncounterEhrPatients = (
  patients: Record<string, any>[] = [],
) => {
  const seen = new Set();
  const result = [];

  for (const patient of patients) {
    const key = newEncounterEhrPatientKey(patient) || `anon_${result.length}`;
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(patient);
  }

  return result;
};

export async function searchNewEncounterEhrPatientsByName(
  companyId: unknown,
  searchText: unknown,
  {resultLimit = DEFAULT_RESULT_LIMIT}: Record<string, any> = {},
) {
  const company = cleanValue(companyId);
  const prefix = newEncounterFirstNamePrefix(searchText);
  if (!company || !prefix) return [];

  const safeResultLimit = Math.max(
    1,
    Number(resultLimit) || DEFAULT_RESULT_LIMIT,
  );
  const fetchLimit = Math.max(
    safeResultLimit,
    safeResultLimit * FIRESTORE_FETCH_MULTIPLIER,
  );
  const patientsRef = collection(db, 'EhrCache', company, 'patients');
  const startedAt = Date.now();
  let snapshot;
  try {
    snapshot = await getDocs(
      query(
        patientsRef,
        where('search_prefixes', 'array-contains', prefix),
        orderBy('search_sort_name', 'asc'),
        firestoreLimit(fetchLimit),
      ),
    );
  } catch (error) {
    log.error('new_encounter_search_failed', {
      query_length: cleanValue(searchText).length,
      prefix_length: prefix.length,
      duration_ms: Date.now() - startedAt,
      ...describeError(error),
    });
    throw error;
  }

  const patients = snapshot.docs.map((docSnap: any) =>
    mapNewEncounterEhrPatient(docSnap.id, docSnap.data()),
  );

  const results = dedupeNewEncounterEhrPatients(
    patients.filter(patient =>
      newEncounterPatientMatchesNamePrefix(searchText, patient),
    ),
  ).slice(0, safeResultLimit);

  log.info('new_encounter_search_completed', {
    query_length: cleanValue(searchText).length,
    prefix_length: prefix.length,
    fetched_count: patients.length,
    results_count: results.length,
    duration_ms: Date.now() - startedAt,
  });

  return results;
}
